import React from 'react';
import { Edit as EditIcon } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Divider, Stack, Typography } from '@mui/material';

interface AttestationEdit {
  note_section: string;
  rationale: string;
  rule: string;
  text: string;
  title: string;
}

interface AttestationEditCardProps {
  attestationEdit: AttestationEdit;
  onAccept: () => void;
  onReject: () => void;
}

export const AttestationEditCard: React.FC<AttestationEditCardProps> = ({
  attestationEdit,
  onAccept,
  onReject,
}) => {
  return (
    <Card
      sx={{
        width: 400,
        minWidth: 400,
        maxWidth: 500,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'borderColors.primary',
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        bgcolor: 'background.paper',
        '&:hover': {
          boxShadow: 1,
          borderColor: 'info.main',
        },
      }}
    >
      {/* Header Section with Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          pb: 1,
          bgcolor: 'backgroundColors.secondary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'info.main',
            color: 'textColors.brandContrast',
            flexShrink: 0,
          }}
        >
          <EditIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              lineHeight: 1.25,
              color: 'textColors.darkText',
              mb: 0.25,
            }}
          >
            {attestationEdit.title}
          </Typography>
          <Typography variant="caption" sx={{ color: 'textColors.lightText', fontSize: '0.6875rem' }}>
            Suggested Edit
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'borderColors.primary' }} />

      {/* Content Section */}
      <CardContent sx={{ p: 1.5, pt: 1, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        <Stack spacing={1}>
          {/* Rationale */}
          <Box>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 600,
                color: 'textColors.lightText',
                fontSize: '0.625rem',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                display: 'block',
                mb: 0.25,
              }}
            >
              Rationale
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: 'textColors.darkText' }}>
              {attestationEdit.rationale}
            </Typography>
          </Box>

          {/* Text Content */}
          <Box>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 600,
                color: 'textColors.lightText',
                fontSize: '0.625rem',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                display: 'block',
                mb: 0.25,
              }}
            >
              Suggested Text
            </Typography>
            <Box
              sx={{
                p: 0.75,
                borderRadius: 1,
                bgcolor: 'backgroundColors.tertiary',
                border: '1px solid',
                borderColor: 'borderColors.lightprimary',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.6875rem',
                  lineHeight: 1.5,
                  color: 'textColors.darkText',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {attestationEdit.text}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </CardContent>

      {/* Footer with Action Buttons */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: 'backgroundColors.secondary',
          borderTop: '1px solid',
          borderColor: 'borderColors.primary',
          display: 'flex',
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={onReject}
          sx={{
            flex: 1,
            fontSize: '0.75rem',
            textTransform: 'none',
            borderColor: 'borderColors.primary',
            color: 'textColors.darkText',
            '&:hover': {
              borderColor: 'borderColors.brand',
              bgcolor: 'action.hover',
            },
          }}
        >
          Reject
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={onAccept}
          sx={{
            flex: 1,
            fontSize: '0.75rem',
            textTransform: 'none',
            bgcolor: 'info.main',
            '&:hover': {
              bgcolor: 'info.dark',
            },
          }}
        >
          Accept
        </Button>
      </Box>
    </Card>
  );
};

